import React, { useState, useEffect } from "react";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const RevenueAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState("30"); // Days

  useEffect(() => {
    fetchAnalytics();
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      if (!token) {
        window.location.href = "/login";
        return;
      }

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const response = await axios.get(
        `${API_BASE_URL}/api/revenue/analytics`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            groupBy: parseInt(dateRange) > 90 ? "month" : "day",
          },
        },
      );

      setAnalytics(response.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load analytics");
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/api/revenue/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setAlerts(response.data.alerts);
    } catch (err) {
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <PortalAdminLayout>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            fontSize: "16px",
            color: "#6b7280",
          }}
        >
          Loading revenue analytics...
        </div>
      </PortalAdminLayout>
    );
  }

  if (error) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
          <div
            style={{
              padding: "16px 20px",
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: "8px",
              color: "#c33",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <h1
            style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#111827",
              margin: 0,
            }}
          >
            💰 Revenue Analytics
          </h1>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <label
              htmlFor="dateRange"
              style={{
                fontSize: "13px",
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Time Range
            </label>
            <select
              id="dateRange"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "white",
                cursor: "pointer",
                minWidth: "180px",
              }}
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="180">Last 6 Months</option>
              <option value="365">Last Year</option>
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px",
            marginBottom: "24px",
          }}
        >
          {/* PRIMARY: Platform Transaction Fee Income */}
          <div
            style={{
              backgroundColor: "#ecfdf5",
              borderRadius: "10px",
              padding: "14px 16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "2px solid #10b981",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span style={{ fontSize: "14px", marginRight: "6px" }}>💵</span>
              <span
                style={{ fontSize: "11px", color: "#065f46", fontWeight: 700 }}
              >
                Platform Fee Income (Period)
              </span>
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#059669",
                marginBottom: "4px",
              }}
            >
              {formatCurrency(analytics?.summary?.totalPlatformRevenue)}
            </div>
            <div style={{ fontSize: "11px", color: "#065f46" }}>
              Your cut from product sales &amp; bookings
            </div>
            {analytics?.summary?.productFeePercent > 0 && (
              <div
                style={{ fontSize: "11px", color: "#6b7280", marginTop: "4px" }}
              >
                Product fee rate:{" "}
                <strong style={{ color: "#059669" }}>
                  {analytics.summary.productFeePercent}%
                </strong>
              </div>
            )}
          </div>

          {/* Subscription Revenue (MRR) */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "10px",
              padding: "14px 16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span style={{ fontSize: "14px", marginRight: "6px" }}>🔄</span>
              <span
                style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600 }}
              >
                Monthly Recurring Revenue
              </span>
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#9c27b0",
                marginBottom: "4px",
              }}
            >
              {formatCurrency(analytics?.summary?.subscriptionRevenue)}/mo
            </div>
            <div style={{ fontSize: "11px", color: "#6b7280" }}>
              {analytics?.summary?.activeSubscriptions || 0} active
              subscriptions
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "#10b981",
                marginTop: "4px",
                fontWeight: 600,
              }}
            >
              {formatCurrency(
                analytics?.byType?.subscriptions?.annualRevenue || 0,
              )}{" "}
              ARR
            </div>
          </div>

          {/* Merchant Sales Volume */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "10px",
              padding: "14px 16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span style={{ fontSize: "14px", marginRight: "6px" }}>📈</span>
              <span
                style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600 }}
              >
                Merchant Sales Volume
              </span>
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#1a659e",
                marginBottom: "4px",
              }}
            >
              {formatCurrency(analytics?.summary?.totalGrossSales)}
            </div>
            <div style={{ fontSize: "11px", color: "#6b7280" }}>
              Total paid by customers to shops
            </div>
          </div>

          {/* Transactions */}
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "10px",
              padding: "14px 16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span style={{ fontSize: "14px", marginRight: "6px" }}>🛒</span>
              <span
                style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600 }}
              >
                Transactions
              </span>
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#6366f1",
                marginBottom: "4px",
              }}
            >
              {analytics?.summary?.totalTransactions}
            </div>
            <div style={{ fontSize: "11px", color: "#6b7280" }}>
              All payment types in period
            </div>
          </div>
        </div>

        {/* Lost Revenue Card - Cancelled/No-Show */}
        {analytics?.summary?.lostRevenue > 0 && (
          <div
            style={{
              padding: "24px",
              marginBottom: "24px",
              backgroundColor: "#fff3e0",
              border: "2px solid #ff9800",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span style={{ fontSize: "18px" }}>⚠️</span>
                <div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#e65100",
                      marginBottom: "4px",
                    }}
                  >
                    Lost Revenue (Cancelled/No-Show)
                  </div>
                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                    {analytics.summary.lostTransactions} transactions that
                    didn't complete
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#e65100",
                }}
              >
                {formatCurrency(analytics.summary.lostRevenue)}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "24px",
                flexWrap: "wrap",
                fontSize: "13px",
                color: "#6b7280",
              }}
            >
              <div>
                📅 Cancelled Bookings:{" "}
                <strong style={{ color: "#374151" }}>
                  {formatCurrency(analytics?.lostRevenue?.bookings?.amount)}
                </strong>{" "}
                ({analytics?.lostRevenue?.bookings?.count} bookings)
              </div>
              <div>
                🛒 Cancelled Orders:{" "}
                <strong style={{ color: "#374151" }}>
                  {formatCurrency(analytics?.lostRevenue?.orders?.amount)}
                </strong>{" "}
                ({analytics?.lostRevenue?.orders?.count} orders)
              </div>
            </div>
          </div>
        )}

        {/* Revenue by Type */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #e5e7eb",
            marginBottom: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "13px",
              fontWeight: 700,
              color: "#111827",
              marginBottom: "6px",
            }}
          >
            Platform Income Breakdown
          </h2>
          <p
            style={{ fontSize: "11px", color: "#6b7280", marginBottom: "14px" }}
          >
            How much the platform earned from each revenue stream in the
            selected period
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "20px",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "6px",
                  gap: "6px",
                }}
              >
                <span style={{ fontSize: "13px" }}>🛒</span>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Product Fee Income
                </span>
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#059669",
                  marginBottom: "3px",
                }}
              >
                {formatCurrency(analytics?.byType?.orders?.revenue)}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#6b7280",
                  marginBottom: "3px",
                }}
              >
                {analytics?.byType?.orders?.count} orders •{" "}
                {formatCurrency(analytics?.byType?.orders?.sales)} merchant
                sales
              </div>
              {analytics?.summary?.productFeePercent > 0 && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#059669",
                    fontWeight: 600,
                  }}
                >
                  @ {analytics.summary.productFeePercent}% product fee rate
                </div>
              )}
            </div>

            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "6px",
                  gap: "6px",
                }}
              >
                <span style={{ fontSize: "13px" }}>🎁</span>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Package Fee Income
                </span>
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#059669",
                  marginBottom: "3px",
                }}
              >
                {formatCurrency(analytics?.byType?.packages?.revenue)}
              </div>
              <div style={{ fontSize: "11px", color: "#6b7280" }}>
                {analytics?.byType?.packages?.count} packages •{" "}
                {formatCurrency(analytics?.byType?.packages?.sales)} merchant
                sales
              </div>
            </div>

            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "6px",
                  gap: "6px",
                }}
              >
                <span style={{ fontSize: "13px" }}>🔄</span>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Subscription Income
                </span>
              </div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#9c27b0",
                  marginBottom: "3px",
                }}
              >
                {formatCurrency(
                  analytics?.byType?.subscriptions?.monthlyRevenue,
                )}
                /mo
              </div>
              <div style={{ fontSize: "11px", color: "#6b7280" }}>
                {analytics?.byType?.subscriptions?.count} subscribers •{" "}
                {formatCurrency(
                  analytics?.byType?.subscriptions?.annualRevenue,
                )}{" "}
                ARR
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        {analytics?.byType?.subscriptions?.count > 0 && (
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
              marginBottom: "24px",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#111827",
                marginBottom: "20px",
              }}
            >
              💳 Active Subscriptions
            </h2>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Customer
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: 600,
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Monthly Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.byType.subscriptions.details.map((sub, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid #f3f4f6",
                        backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb",
                      }}
                    >
                      <td
                        style={{
                          padding: "12px",
                          color: "#374151",
                          fontWeight: 500,
                        }}
                      >
                        {sub.name}
                      </td>
                      <td style={{ padding: "12px" }}>
                        <span
                          style={{
                            padding: "4px 8px",
                            backgroundColor:
                              sub.type === "shop" ? "#dbeafe" : "#fce7f3",
                            color: sub.type === "shop" ? "#1e40af" : "#be185d",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          {sub.type}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontWeight: 600,
                          color: "#10b981",
                        }}
                      >
                        {formatCurrency(sub.monthlyAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* High-Value Transactions */}
        {analytics?.recentHighValue && analytics.recentHighValue.length > 0 && (
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "1px solid #e5e7eb",
              marginBottom: "24px",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#111827",
                marginBottom: "20px",
              }}
            >
              Recent High-Value Transactions ($10+ Platform Fee)
            </h2>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Order #
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Shop
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: 600,
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Total Amount
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontWeight: 600,
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Platform Fee
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.recentHighValue.map((order, idx) => (
                    <tr
                      key={order._id}
                      style={{
                        borderBottom: "1px solid #f3f4f6",
                        backgroundColor: idx % 2 === 0 ? "white" : "#f9fafb",
                      }}
                    >
                      <td style={{ padding: "12px" }}>
                        <span
                          style={{
                            padding: "4px 8px",
                            backgroundColor: "#dbeafe",
                            color: "#1e40af",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {order.orderNumber}
                        </span>
                      </td>
                      <td style={{ padding: "12px", color: "#374151" }}>
                        {order.shops?.[0]?.shopId?.name || "N/A"}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          color: "#374151",
                          fontWeight: 600,
                        }}
                      >
                        {formatCurrency(order.totalAmount)}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          color: "#10b981",
                          fontWeight: 700,
                        }}
                      >
                        {formatCurrency(order.platformFee)}
                      </td>
                      <td style={{ padding: "12px", color: "#6b7280" }}>
                        {formatDate(order.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Zero-Fee Alerts */}
        {alerts &&
          (alerts.zeroFeeOrders?.length > 0 ||
            alerts.zeroFeeBookings?.length > 0) && (
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                border: "1px solid #e5e7eb",
                marginBottom: "24px",
              }}
            >
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#e3f2fd",
                  border: "1px solid #2196f3",
                  borderRadius: "8px",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#1565c0",
                    marginBottom: "8px",
                  }}
                >
                  📊 Platform Bookings Overview
                </div>
                <div style={{ fontSize: "14px", color: "#1565c0" }}>
                  Showing all bookings from the last 7 days (
                  {alerts.totalAlerts} transactions). Platform operates on a
                  subscription-based model.
                </div>
              </div>

              {alerts.zeroFeeOrders?.length > 0 && (
                <>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#111827",
                      marginBottom: "16px",
                      marginTop: "24px",
                    }}
                  >
                    Product Orders ({alerts.zeroFeeOrders.length})
                  </h3>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <thead>
                        <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Order #
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Shop
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Amount
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Platform Fee
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {alerts.zeroFeeOrders.slice(0, 10).map((order, idx) => (
                          <tr
                            key={order._id}
                            style={{
                              borderBottom: "1px solid #f3f4f6",
                              backgroundColor:
                                idx % 2 === 0 ? "white" : "#f9fafb",
                            }}
                          >
                            <td style={{ padding: "12px", color: "#374151" }}>
                              {order.orderNumber}
                            </td>
                            <td style={{ padding: "12px", color: "#374151" }}>
                              {order.shops?.[0]?.shopId?.name || "N/A"}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "right",
                                color: "#374151",
                                fontWeight: 600,
                              }}
                            >
                              {formatCurrency(order.totalAmount)}
                            </td>
                            <td
                              style={{
                                padding: "12px",
                                textAlign: "right",
                                color:
                                  order.platformFee > 0 ? "#10b981" : "#6b7280",
                                fontWeight: 700,
                              }}
                            >
                              {formatCurrency(order.platformFee)}
                            </td>
                            <td style={{ padding: "12px", color: "#6b7280" }}>
                              {formatDate(order.createdAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {alerts.zeroFeeBookings?.length > 0 && (
                <>
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#111827",
                      marginBottom: "16px",
                      marginTop: "32px",
                    }}
                  >
                    Service Bookings ({alerts.zeroFeeBookings.length})
                  </h3>
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                      }}
                    >
                      <thead>
                        <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Booking #
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Shop
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Amount
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "right",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Platform Fee
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                              fontSize: "13px",
                            }}
                          >
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {alerts.zeroFeeBookings
                          .slice(0, 10)
                          .map((booking, idx) => (
                            <tr
                              key={booking._id}
                              style={{
                                borderBottom: "1px solid #f3f4f6",
                                backgroundColor:
                                  idx % 2 === 0 ? "white" : "#f9fafb",
                              }}
                            >
                              <td
                                style={{
                                  padding: "12px",
                                  color: "#374151",
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                }}
                              >
                                {booking.bookingNumber || booking._id}
                              </td>
                              <td style={{ padding: "12px", color: "#374151" }}>
                                {booking.shopId?.businessId?.name ||
                                  booking.shopId?.name ||
                                  "N/A"}
                              </td>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "right",
                                  color: "#374151",
                                  fontWeight: 600,
                                }}
                              >
                                {formatCurrency(booking.totalAmount)}
                              </td>
                              <td
                                style={{
                                  padding: "12px",
                                  textAlign: "right",
                                  color:
                                    booking.platformFee > 0
                                      ? "#10b981"
                                      : "#6b7280",
                                  fontWeight: 700,
                                }}
                              >
                                {formatCurrency(booking.platformFee)}
                              </td>
                              <td style={{ padding: "12px", color: "#6b7280" }}>
                                {formatDate(booking.createdAt)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
      </div>
    </PortalAdminLayout>
  );
};

export default RevenueAnalytics;
